<script setup>
import {
  differenceInDays,
  format,
  isBefore,
  getMonth,
  isSameMonth,
} from "date-fns";

const { program, wishlistId } = defineProps({
  program: Object,
  wishlistId: String,
});

const { getSession } = useAuth();

const removeWishlist = async () => {
  await useApiFetch(`wishlists/${wishlistId}`, {
    method: "DELETE",
    async onResponse() {
      refreshNuxtData("program-wishlists");
      await getSession();
    },
  });
};

const nearestIntakeIndex = ref(-1);

const getNearestEndDate = (intakeDates) => {
  const now = new Date();

  // Find index of nearest end date that's in the future
  const nearestIndex = intakeDates?.findIndex((intake) => {
    const endDate = new Date(intake.end_date);
    // Set year to current year to only compare month and day
    endDate.setFullYear(now.getFullYear());
    // If date has passed this year, check next year
    if (endDate < now) {
      endDate.setFullYear(now.getFullYear() + 1);
    }
    return endDate > now;
  });

  if (nearestIndex !== -1) {
    nearestIntakeIndex.value = nearestIndex;
  }

  if (nearestIndex === -1 || !intakeDates?.length) {
    return new Date();
  }

  const endDate = new Date(intakeDates[nearestIndex].end_date);
  endDate.setFullYear(now.getFullYear());
  if (endDate < now) {
    endDate.setFullYear(now.getFullYear() + 1);
  }

  return endDate;
};

const getNearestStartDate = (intakeDates) => {
  const now = new Date();

  if (nearestIntakeIndex.value !== -1 && intakeDates?.length) {
    const startDate = new Date(
      intakeDates[nearestIntakeIndex.value].start_date,
    );
    const endDate = new Date(getNearestEndDate(intakeDates));
    const currentYear = now.getFullYear();

    if (isBefore(startDate, endDate)) {
      startDate.setFullYear(currentYear + 1);
    } else {
      startDate.setFullYear(currentYear);
    }

    // Additional check if date has already passed
    if (startDate < now) {
      startDate.setFullYear(currentYear + 1);
      endDate.setFullYear(currentYear + 1);
    }

    return startDate;
  }

  return new Date();
};

function formatDate(inputDate, currentDate = new Date()) {
  // if (isSameMonth(inputDate, currentDate)) {
  //   const daysLeft = differenceInDays(inputDate, currentDate);

  //   if (daysLeft < 0) {
  //     return format(inputDate, "dd MMM yyyy");
  //   }

  //   if (daysLeft === 0) {
  //     return "Deadline today";
  //   }

  //   return `${daysLeft} day(s) left`;
  // } else if (isBefore(inputDate, currentDate)) {
  //   return "Deadline over";
  // }

  return format(inputDate, "MMM yyyy");
}

function getAttributeData(key) {
  const attribute = program?.attributes?.data?.find((d) => d.key === key);
  return attribute ? attribute.value : "";
}
</script>

<template>
  <div class="rounded-lg bg-white border border-slate-200">
    <div class="px-4 py-4 md:px-6">
      <!-- Header Row -->
      <div class="flex justify-between items-start md:items-center gap-2">
        <Button
          :label="program?.name"
          class="hover:underline !pl-0 pr-5 text-lg md:text-xl !font-bold text-primary-500 !text-left"
          link
          @click="navigateTo(`/programs/${program?.slug}`)"
          :disabled="program?.deleted_at"
        />
        <Button
          icon="pi pi-trash"
          outlined
          size="small"
          @click="removeWishlist"
          class="hidden md:flex"
        />
        <Button
          icon="pi pi-trash"
          text
          size="small"
          @click="removeWishlist"
          class="flex md:hidden"
        />
      </div>

      <!-- Meta Row -->
      <div
        class="flex items-center flex-wrap gap-2 text-slate-800 text-sm mt-3 mb-5"
      >
        <Button
          :label="program?.university?.name"
          class="text-blue-400 hover:underline !pl-0 text-left"
          link
          @click="navigateTo(`/universities/${program?.university?.slug}`)"
        />

        <span class="border-l border-slate-200 h-4"></span>
        <p>{{ program?.university?.type }}</p>

        <span class="hidden md:block border-l border-slate-200 h-4"></span>
        <p class="border-slate-200">
          <span v-if="program?.university?.ranking?.number === 0">
            Ranking Not Available
          </span>
          <span v-else>
            {{
              `${program?.university?.ranking?.name} ${program?.university?.ranking?.number}`
            }}
          </span>
        </p>
      </div>

      <!-- Stat Section -->
      <div class="flex flex-col md:flex-row md:items-center gap-1">
        <!-- Intake -->
        <div>
          <p class="text-sm text-slate-600">Next intake</p>
          <p class="font-bold text-primary-500 text-base md:text-lg">
            {{
              program?.intakes?.data?.length > 0
                ? formatDate(getNearestStartDate(program?.intakes?.data))
                : "Data not available"
            }}
          </p>
        </div>

        <!-- Divider -->
        <Divider
          type="solid"
          layout="vertical"
          class="hidden md:block before:w-px before:h-8 before:bg-slate-200"
        />

        <!-- Deadline -->
        <div>
          <p class="text-sm text-slate-600">Deadline</p>
          <p class="font-bold text-primary-500 text-base md:text-lg">
            {{
              program?.intakes?.data?.length > 0
                ? formatDate(getNearestEndDate(program?.intakes?.data))
                : "Data not available"
            }}
          </p>
        </div>

        <!-- Divider -->
        <Divider
          type="solid"
          layout="vertical"
          class="hidden md:block before:w-px before:h-8 before:bg-slate-200"
        />

        <!-- Duration -->
        <div>
          <p class="text-sm text-slate-600">Total duration</p>
          <p class="font-bold text-primary-500 text-base md:text-lg">
            {{ getAttributeData("duration") }}
          </p>
        </div>

        <!-- Divider -->
        <Divider
          type="solid"
          layout="vertical"
          class="hidden md:!mx-2 md:block before:w-px before:h-8 before:bg-slate-200"
        />

        <!-- Tuition -->
        <div>
          <p class="text-sm text-slate-600">Tuition fees</p>
          <p class="font-bold text-primary-500 md:text-lg">
            <span>{{ getTuitionFee(program?.tuition_fee) }}</span>
            <span v-if="program?.tuition_fee?.approach" class="text-xs">
              (Approx)
            </span>
            <span class="text-sm"
              >/ {{ program?.tuition_fee?.recurring_payment_type }}</span
            >
          </p>
        </div>
      </div>
    </div>

    <!-- CTA Footer -->
    <!-- <div class="p-4 bg-white flex justify-end rounded-lg">
      <Button
        icon="pi pi-angle-right"
        icon-pos="right"
        label="Read more"
        :pt="{ icon: 'text-xs' }"
        class="w-full md:w-fit"
        @click="navigateTo(`programs/${program?.slug}`)"
      />
    </div> -->
  </div>
</template>
